import { useState, useEffect } from 'react';

import { useGetVendorQuoteStatuses } from './useGetVendorQuoteStatuses';

const DONE_STATUSES = ['FAILED', 'SUCCESS'] as const;

export type UsePollVendorQuoteStatusParams = {
  vendorQuoteUuids?: string[];
};

export const usePollVendorQuoteStatus = ({
  vendorQuoteUuids,
}: UsePollVendorQuoteStatusParams = {}) => {
  const [pollingStatuses, setPollingStatuses] = useState<
    Record<string, boolean>
  >({});
  const normalizedVendorQuoteUuids = (vendorQuoteUuids || []).join('|');

  useEffect(() => {
    const nextPollingStatuses: typeof pollingStatuses = {};
    if (normalizedVendorQuoteUuids) {
      normalizedVendorQuoteUuids.split('|').forEach((uuid) => {
        nextPollingStatuses[uuid] = true;
      });
    }
    setPollingStatuses(nextPollingStatuses);
  }, [normalizedVendorQuoteUuids]);

  const pollingUuids = Object.keys(pollingStatuses).filter(
    (uuid) => pollingStatuses[uuid],
  );

  useGetVendorQuoteStatuses(
    {
      uuids: pollingUuids,
    },
    {
      queryOptions: {
        refetchInterval: pollingUuids.length ? 5000 : 0,
        onSuccess: (response) => {
          const finishedPollingUuids: typeof pollingStatuses = {};
          pollingUuids.forEach((pollingRequestUuid) => {
            const vendorQuoteStatus = (response?.requestStatuses || []).find(
              ({ uuid }) => uuid === pollingRequestUuid,
            );
            // we are no longer watching this vendor quote if it does not have a record, or it's done
            if (
              !vendorQuoteStatus ||
              DONE_STATUSES.includes(
                vendorQuoteStatus.requestStatus as (typeof DONE_STATUSES)[number],
              )
            ) {
              finishedPollingUuids[pollingRequestUuid] = false;
            }
          });

          if (Object.keys(finishedPollingUuids).length) {
            // merge into the next state
            setPollingStatuses((prevState) => ({
              ...prevState,
              ...finishedPollingUuids,
            }));
          }
        },
        enabled: !!pollingUuids.length,
      },
    },
  );

  return pollingStatuses;
};
